/**
 * Stripe: which Stripe account bills Vloud plans, and the switch from the sandbox to live.
 *
 * The owner pastes a key here instead of editing the server's environment and restarting it. The server checks the key with Stripe
 * before keeping it, stores it sealed, and starts using it at once. A LIVE key (real money) needs the words GO LIVE typed, and the same
 * goes for creating the products, prices and webhook in a live account. Nothing on this page ever shows a key or a webhook secret again
 * after it is saved, only the last four characters.
 */
import { type JSX, useCallback, useEffect, useId, useMemo, useState } from 'react';
import { CreditCard } from 'lucide-react';
import { AdminApiClient, type StripeSetupPrice, type StripeSetupResult, type StripeStatus, type StripeVerifyResult } from '../api';
import { failureFrom, userMessageFor, type Failure } from '../lib/api-state';
import { usePermissions } from '../lib/permissions';
import { FetchState } from '../components/ui/FetchState';
import { PageHeader } from '../components/ui/PageHeader';
import { AccessNotice } from '../components/ui/AccessNotice';
import { Button } from '../components/ui/Button';
import { useSession } from '../session';

const SECTION: React.CSSProperties = { border: '1px solid var(--border)', borderRadius: 8, padding: 20, marginBottom: 16, background: 'var(--surface, transparent)' };
const H2: React.CSSProperties = { margin: '0 0 4px', fontSize: '0.95rem' };
const HELP: React.CSSProperties = { color: 'var(--text-muted)', fontSize: '0.78rem', margin: '0 0 14px', lineHeight: 1.5 };
const INPUT: React.CSSProperties = { width: '100%', padding: '7px 9px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.8125rem' };

const VISUALLY_HIDDEN: React.CSSProperties = { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' };
const usd = (cents: number | null | undefined): string => (cents == null ? '—' : `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}`);
const modeOf = (key: string): 'test' | 'live' | null => (/^(sk|rk)_live_/.test(key) ? 'live' : /^(sk|rk)_test_/.test(key) ? 'test' : null);
const PLAN_LABEL = { business: 'Business', enterprise: 'Enterprise' } as const;

function ModeBadge({ status }: { status: StripeStatus }): JSX.Element {
  const live = status.mode === 'live';
  const test = status.mode === 'test';
  const tone = live ? { fg: 'var(--success)', bg: 'var(--success-soft)' } : test ? { fg: 'var(--warning)', bg: 'var(--warning-soft)' } : { fg: 'var(--text-muted)', bg: 'rgba(148,163,184,0.1)' };
  return (
    <div data-testid="stripe-mode" style={{ display: 'inline-flex', flexDirection: 'column', gap: 2, padding: '10px 16px', borderRadius: 10, background: tone.bg, color: tone.fg, border: `1px solid ${tone.fg}33` }}>
      <strong style={{ fontSize: '1.15rem', letterSpacing: '0.04em' }}>{live ? 'LIVE' : test ? 'TEST' : 'NOT CONNECTED'}</strong>
      <span style={{ fontSize: '0.75rem' }}>{live ? 'Real cards are charged' : test ? 'Sandbox: no real money moves' : 'Checkout and billing are off'}</span>
    </div>
  );
}

function Row({ label, children, testId }: { label: string; children: React.ReactNode; testId?: string }): JSX.Element {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 12, padding: '7px 0', borderTop: '1px solid var(--border)', fontSize: '0.8125rem' }} data-testid={testId}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{children}</span>
    </div>
  );
}

function Secret({ label, value, onChange, disabled, placeholder, help }: { label: string; value: string; onChange: (v: string) => void; disabled?: boolean; placeholder: string; help?: string }): JSX.Element {
  const id = useId();
  return (
    <div style={{ marginBottom: 14 }}>
      <label htmlFor={id} style={{ display: 'block', fontSize: '0.8125rem', marginBottom: 4 }}>{label}</label>
      <input id={id} type="password" autoComplete="off" spellCheck={false} value={value} placeholder={placeholder} disabled={disabled}
        onChange={(e) => onChange(e.target.value)} style={INPUT} />
      {help && <p style={{ ...HELP, margin: '5px 0 0', fontSize: '0.72rem' }}>{help}</p>}
    </div>
  );
}

export function StripePage(): JSX.Element {
  const session = useSession();
  const api = useMemo(
    () => new AdminApiClient({ baseUrl: import.meta.env.VITE_LICENSE_SERVER_URL ?? '', getToken: () => session.state.token }),
    [session.state.token],
  );
  const perms = usePermissions();
  const canView = perms.canReadAdminData;
  const canChange = perms.canOwner;

  const [status, setStatus] = useState<StripeStatus | null>(null);
  const [loadErr, setLoadErr] = useState<Failure | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  // connect form
  const [key, setKey] = useState('');
  const [webhook, setWebhook] = useState('');
  const [confirm, setConfirm] = useState('');
  const [check, setCheck] = useState<StripeVerifyResult | null>(null);
  const [busy, setBusy] = useState<null | 'check' | 'save' | 'setup' | 'look' | 'clear' | 'refresh'>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // plans form
  const [bizUsd, setBizUsd] = useState('');
  const [entUsd, setEntUsd] = useState('');
  const [setupConfirm, setSetupConfirm] = useState('');
  const [setupResult, setSetupResult] = useState<StripeSetupResult | null>(null);
  const [setupErr, setSetupErr] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoadErr(null);
    try { setStatus(await api.stripeSettings()); } catch (e) { setLoadErr(failureFrom(e, 'the Stripe settings')); } finally { setLoading(false); }
  }, [api]);
  useEffect(() => {
    if (!canView) { setLoading(false); return; }
    setLoading(true);
    void load();
  }, [canView, load, reloadKey]);

  const typedMode = modeOf(key.trim());
  const phrase = status?.live_confirmation ?? 'GO LIVE';
  const liveTyped = typedMode === 'live';
  const confirmed = confirm.trim() === phrase;
  const canSave = canChange && busy === null && (key.trim() !== '' || webhook.trim() !== '') && (!liveTyped || confirmed) && (status?.can_store ?? false);

  const run = async (what: NonNullable<typeof busy>, fn: () => Promise<void>): Promise<void> => {
    setBusy(what); setMsg(null); setErr(null);
    try { await fn(); } catch (e) { setErr(userMessageFor(e, 'Stripe')); } finally { setBusy(null); }
  };

  const doCheck = (): Promise<void> => run('check', async () => {
    const r = await api.verifyStripeKey(key.trim() || undefined);
    setCheck(r);
  });

  const doSave = (): Promise<void> => run('save', async () => {
    const body: { secret_key?: string; webhook_secret?: string; confirm?: string } = {};
    if (key.trim()) body.secret_key = key.trim();
    if (webhook.trim()) body.webhook_secret = webhook.trim();
    if (liveTyped) body.confirm = confirm.trim();
    const r = await api.saveStripeSettings(body);
    setStatus(r.status);
    setKey(''); setWebhook(''); setConfirm(''); setCheck(null);
    setMsg(r.status.mode === 'live'
      ? 'Saved. The server is now using the LIVE key. No restart needed.'
      : r.status.mode === 'test' ? 'Saved. The server is now using the sandbox key. No restart needed.' : 'Saved.');
  });

  const amounts = (): { business?: number; enterprise?: number } => {
    const out: { business?: number; enterprise?: number } = {};
    const b = Number(bizUsd); const e = Number(entUsd);
    if (bizUsd.trim() && Number.isFinite(b) && b > 0) out.business = b;
    if (entUsd.trim() && Number.isFinite(e) && e > 0) out.enterprise = e;
    return out;
  };

  const doSetup = (checkOnly: boolean): Promise<void> => run(checkOnly ? 'look' : 'setup', async () => {
    setSetupErr(null);
    try {
      const r = await api.setupStripe({ amounts_usd: amounts(), check_only: checkOnly, ...(status?.mode === 'live' && !checkOnly ? { confirm: setupConfirm.trim() } : {}) });
      setSetupResult(r); setStatus(r.status); setSetupConfirm('');
      if (!checkOnly) setMsg(r.webhook.secret_saved ? 'Done. The webhook endpoint was created and its signing secret was saved for you.' : 'Done.');
    } catch (e) { setSetupErr(userMessageFor(e, 'Stripe')); }
  });

  const doClear = (): Promise<void> => run('clear', async () => {
    if (!window.confirm('Forget the Stripe keys saved here? The server goes back to the Stripe settings in its environment (or to "not connected").')) return;
    const r = await api.clearStripeSettings();
    setStatus(r.status); setCheck(null); setSetupResult(null);
    setMsg('The keys saved here were removed.');
  });

  const doRefresh = (): Promise<void> => run('refresh', async () => {
    const r = await api.refreshStripePrices();
    setStatus(r.status);
    setMsg('Prices looked up again.');
  });

  return (
    <div style={{ maxWidth: 820 }}>
      <PageHeader
        title="Stripe"
        icon={CreditCard}
        accent="#635bff"
        subtitle="The Stripe account that bills Vloud plans. Paste a key here to switch between the sandbox and live. No server access needed."
      />

      {!canView ? (
        <FetchState error={{ kind: 'no_access', retryable: false, message: perms.role === 'owner' || perms.role === 'admin' ? 'Finish two-factor sign-in to view the Stripe settings.' : 'The Stripe settings are available to Owner and Admin accounts.' }} />
      ) : loadErr ? (
        <FetchState error={loadErr} onRetry={() => setReloadKey((k) => k + 1)} />
      ) : loading || !status ? (
        <p role="status" className="muted">Loading…</p>
      ) : (
        <>
          <AccessNotice reason={canChange ? null : 'Only the Owner can change the Stripe connection. You can see its state here.'} />

          {/* ── what the server is using now ── */}
          <section style={SECTION} aria-labelledby="stripe-now">
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
              <ModeBadge status={status} />
              <div style={{ minWidth: 0 }}>
                <h2 id="stripe-now" style={H2}>In use right now</h2>
                <p style={{ ...HELP, margin: 0 }}>
                  {status.configured
                    ? <>Key <code data-testid="stripe-key-tail">{(status.key_prefix ?? '') + '…' + (status.key_tail ?? '')}</code>{' '}
                      {status.source === 'admin' ? 'saved on this page' : 'from the server\'s environment'}.</>
                    : 'No Stripe key is set. Customers cannot upgrade and billing actions are off.'}
                </p>
              </div>
            </div>

            {status.stored.present && !status.stored.readable && (
              <p role="alert" className="err" style={{ fontSize: '0.8rem', margin: '0 0 10px' }} data-testid="stripe-unreadable">
                A key saved here can no longer be read (the server&apos;s encryption key changed). {status.configured ? 'The environment key is being used instead. ' : ''}Enter the key again below.
              </p>
            )}
            {!status.can_store && (
              <p role="note" style={{ fontSize: '0.8rem', margin: '0 0 10px', padding: '8px 12px', borderLeft: '3px solid var(--warning)' }} data-testid="stripe-cannot-store">
                This server has no encryption key for storing secrets, so keys cannot be saved here yet. Set <code>VLOUD_SETTINGS_KEY</code> (64 hex characters) on the licence server and restart it once.
                Until then Stripe comes from the server&apos;s environment.
              </p>
            )}

            {status.stored.account_name && status.source === 'admin' && <Row label="Stripe account">{status.stored.account_name} <span className="muted">({status.stored.account_id})</span></Row>}
            <Row label="Webhook" testId="stripe-webhook-row">
              {status.webhook_configured
                ? <>Signing secret saved (…{status.webhook_tail}). Stripe events change licences.</>
                : <span className="err">No signing secret. Cancellations, failed payments and refunds from Stripe are ignored until one is saved.</span>}
              <div className="muted" style={{ fontSize: '0.75rem', marginTop: 2 }}>Stripe must send events to <code>{status.webhook_url}</code></div>
            </Row>
            {(['business', 'enterprise'] as const).map((plan) => {
              const r = status.prices?.resolved[plan];
              const p = status.prices?.problems.find((x) => x.plan === plan);
              return (
                <Row key={plan} label={`${PLAN_LABEL[plan]} price`} testId={`stripe-price-${plan}`}>
                  {r ? <>{usd(r.unitAmount)} / {r.interval ?? 'month'} <span className="muted">({r.priceId})</span></>
                    : status.configured ? <span className={plan === 'business' ? 'err' : 'muted'}>No price in this Stripe account yet{p ? `: ${p.detail}` : ''}</span>
                      : <span className="muted">—</span>}
                </Row>
              );
            })}
            {status.mode === 'live' && (status.linked_subscriptions ?? 0) > 0 && (
              <p style={{ ...HELP, margin: '10px 0 0' }}>
                {status.linked_subscriptions} licence{status.linked_subscriptions === 1 ? ' is' : 's are'} linked to a Stripe subscription. Ones bought in the sandbox do not exist in the live account and will show as
                &ldquo;subscription missing&rdquo; under Anomalies. Nothing is revoked because of that.
              </p>
            )}
            <div style={{ marginTop: 12 }}>
              <Button variant="outline" size="sm" disabled={busy !== null || !status.configured} onClick={() => void doRefresh()}>Look the prices up again</Button>
            </div>
          </section>

          {msg && <p role="status" data-testid="stripe-msg" style={{ fontSize: '0.85rem', margin: '0 0 12px' }} className="ok">{msg}</p>}
          {err && <p role="alert" data-testid="stripe-err" className="err" style={{ fontSize: '0.85rem', margin: '0 0 12px' }}>{err}</p>}

          {/* ── connect or switch ── */}
          <section style={SECTION} aria-labelledby="stripe-connect">
            <h2 id="stripe-connect" style={H2}>{status.configured ? 'Switch or replace the key' : 'Connect Stripe'}</h2>
            <p style={HELP}>
              In Stripe open Developers &rsaquo; API keys and copy the secret key (<code>sk_test_…</code> for the sandbox, <code>sk_live_…</code> for live).
              The server asks Stripe whether the key works before it keeps it. It is stored encrypted and never shown again.
            </p>
            <Secret label="Secret key" value={key} onChange={(v) => { setKey(v); setCheck(null); }} disabled={!canChange || busy !== null} placeholder="sk_test_… or sk_live_…" />
            <Secret label="Webhook signing secret (optional)" value={webhook} onChange={setWebhook} disabled={!canChange || busy !== null} placeholder="whsec_…"
              help={'Only needed if the webhook endpoint already exists in this Stripe account. If it does not, use "Create what is missing" below and it is saved for you. Switching between sandbox and live drops the old one, since it belongs to the other account.'} />

            {liveTyped && (
              <div role="group" aria-label="Confirm live key" style={{ border: '1px solid var(--danger)', borderRadius: 8, padding: 12, margin: '0 0 14px', background: 'var(--danger-soft)' }}>
                <strong style={{ fontSize: '0.85rem' }}>This is a LIVE key.</strong>
                <p style={{ ...HELP, margin: '4px 0 8px' }}>Customers will be charged real money as soon as you save. Type <code>{phrase}</code> to confirm.</p>
                <input aria-label={`Type ${phrase} to confirm`} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={phrase} autoComplete="off" style={{ ...INPUT, maxWidth: 220 }} />
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button variant="outline" disabled={!canChange || busy !== null || (!key.trim() && !status.configured)} onClick={() => void doCheck()}>
                {busy === 'check' ? 'Checking…' : key.trim() ? 'Check this key' : 'Check the saved key'}
              </Button>
              <Button disabled={!canSave} onClick={() => void doSave()}>
                {busy === 'save' ? 'Saving…' : typedMode === 'live' ? 'Save and go live' : 'Save'}
              </Button>
            </div>

            {check && (
              <div data-testid="stripe-check" style={{ marginTop: 14, padding: '10px 12px', borderLeft: `3px solid ${check.ok ? 'var(--success)' : 'var(--danger)'}`, fontSize: '0.8125rem' }}>
                {check.ok ? (
                  <>
                    <strong>Stripe accepts this {check.mode === 'live' ? 'LIVE' : 'sandbox'} key.</strong>
                    {check.account?.name && <> Account: {check.account.name}{check.account.country ? ` (${check.account.country})` : ''}.</>}
                    {check.mode === 'live' && check.account?.chargesEnabled === false && <span className="err"> This account cannot take payments yet: finish Stripe&apos;s activation first.</span>}
                    <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                      {(check.prices ?? []).map((p) => (
                        <li key={p.plan}>{PLAN_LABEL[p.plan]}: {p.action === 'exists' || p.action === 'exists_other_amount' ? `price exists (${usd(p.unitAmount)} / month)` : 'no price yet'}</li>
                      ))}
                      {check.webhook && <li>Webhook endpoint: {check.webhook.action === 'exists' ? 'exists' : 'not created yet'}</li>}
                    </ul>
                  </>
                ) : <span className="err">{check.message ?? 'Stripe did not accept this key.'}</span>}
              </div>
            )}
          </section>

          {/* ── products, prices, webhook ── */}
          <section style={SECTION} aria-labelledby="stripe-setup">
            <h2 id="stripe-setup" style={H2}>Plans and webhook in this Stripe account</h2>
            <p style={HELP}>
              Creates what Vloud needs in the account the key belongs to: one monthly price per paid plan and the webhook endpoint with the events Vloud acts on.
              Safe to repeat: it only creates what is missing. Stripe prices cannot be edited, so to change an amount archive the old price in Stripe and run this again.
            </p>
            {!status.configured ? <p className="muted" style={{ fontSize: '0.8rem' }}>Connect a key first.</p> : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 12 }}>
                  <label style={{ fontSize: '0.8125rem' }}>Business, USD per month
                    <input inputMode="decimal" value={bizUsd} onChange={(e) => setBizUsd(e.target.value)} placeholder="e.g. 29" disabled={!canChange || busy !== null} style={{ ...INPUT, display: 'block', marginTop: 4 }} />
                  </label>
                  <label style={{ fontSize: '0.8125rem' }}>Enterprise, USD per month (optional)
                    <input inputMode="decimal" value={entUsd} onChange={(e) => setEntUsd(e.target.value)} placeholder="leave empty if quoted by sales" disabled={!canChange || busy !== null} style={{ ...INPUT, display: 'block', marginTop: 4 }} />
                  </label>
                </div>
                {status.mode === 'live' && (
                  <div style={{ margin: '0 0 12px' }}>
                    <label style={{ fontSize: '0.8125rem' }}>This is your LIVE account. Type <code>{phrase}</code> to create things in it
                      <input value={setupConfirm} onChange={(e) => setSetupConfirm(e.target.value)} placeholder={phrase} autoComplete="off" disabled={!canChange || busy !== null} style={{ ...INPUT, display: 'block', marginTop: 4, maxWidth: 220 }} />
                    </label>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Button variant="outline" disabled={!canChange || busy !== null} onClick={() => void doSetup(true)}>{busy === 'look' ? 'Looking…' : 'Look (no changes)'}</Button>
                  <Button disabled={!canChange || busy !== null || (status.mode === 'live' && setupConfirm.trim() !== phrase)} onClick={() => void doSetup(false)}>
                    {busy === 'setup' ? 'Creating…' : 'Create what is missing'}
                  </Button>
                </div>
                {setupErr && <p role="alert" className="err" data-testid="stripe-setup-err" style={{ fontSize: '0.8rem', marginTop: 10 }}>{setupErr}</p>}
                {setupResult && (
                  <table data-testid="stripe-setup-result" style={{ width: '100%', marginTop: 14, fontSize: '0.8rem', borderCollapse: 'collapse' }}>
                    <tbody>
                      {setupResult.prices.map((p: StripeSetupPrice) => (
                        <tr key={p.plan}>
                          <td style={{ padding: '4px 0', width: 130 }}>{PLAN_LABEL[p.plan]}</td>
                          <td>{({ created: 'Created', exists: 'Already there', exists_other_amount: 'Already there at a different amount (not changed)', missing: 'Skipped: no amount given', would_create: 'Would be created' } as const)[p.action]}</td>
                          <td style={{ textAlign: 'right' }}>{usd(p.unitAmount)}</td>
                        </tr>
                      ))}
                      <tr>
                        <td style={{ padding: '4px 0' }}>Webhook</td>
                        <td>{({ created: 'Created, signing secret saved', exists: 'Already there', updated: 'Events added', missing: 'Missing', would_create: 'Would be created' } as const)[setupResult.webhook.action]}</td>
                        <td />
                      </tr>
                    </tbody>
                  </table>
                )}
              </>
            )}
          </section>

          {/* ── ready for real payments? ── */}
          <section style={SECTION} aria-labelledby="stripe-ready">
            <h2 id="stripe-ready" style={H2}>Ready for real payments?</h2>
            <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0', fontSize: '0.85rem' }} data-testid="stripe-ready">
              {[
                { ok: status.mode === 'live', text: 'A live key is in use' },
                { ok: !!status.prices?.resolved.business, text: 'The Business plan has a price in this Stripe account' },
                { ok: status.webhook_configured, text: 'The webhook signing secret is saved' },
              ].map((c) => (
                <li key={c.text} style={{ padding: '3px 0' }}><span aria-hidden style={{ color: c.ok ? 'var(--success)' : 'var(--danger)', marginRight: 8 }}>{c.ok ? '✓' : '✗'}</span><span style={VISUALLY_HIDDEN}>{c.ok ? 'Done: ' : 'Missing: '}</span>{c.text}</li>
              ))}
            </ul>
            <p style={{ ...HELP, margin: '10px 0 0' }}>After going live, make one real purchase with your own card and refund it from the Stripe dashboard to see the whole path work.</p>
          </section>

          {canChange && status.stored.present && (
            <section style={{ ...SECTION, borderColor: 'transparent', padding: '0 4px' }}>
              <button type="button" disabled={busy !== null} onClick={() => void doClear()}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-muted)', fontSize: '0.78rem', textDecoration: 'underline' }}>
                Forget the keys saved on this page
              </button>
              <p style={{ ...HELP, margin: '4px 0 0' }}>The server goes back to the Stripe settings in its environment, or to &ldquo;not connected&rdquo;.</p>
            </section>
          )}
        </>
      )}
    </div>
  );
}
